import React from 'react'
import styled from 'styled-components'

const MapContainerStyled = styled.div<{ $isDimmed?: boolean }>`
  height: 100%;
  width: 100%;
  position: relative;
  background: #1a1a1a;
  overflow: hidden;
  touch-action: none;
  border-radius: 8px;

  .leaflet-container {
    height: 100%;
    width: 100%;
    background: #1a1a1a;
    transition: filter 0.2s ease;
    filter: ${(props) => (props.$isDimmed ? 'brightness(0.9)' : 'brightness(1)')};
  }

  #map {
    height: 100%;
    width: 100%;
    z-index: 1;
  }
`

const DropZoneOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: none;
  backdrop-filter: blur(4px);
`

const DropZoneMessage = styled.div`
  padding: 40px 60px;
  background: rgba(26, 26, 26, 0.98);
  border: 3px dashed rgba(59, 130, 246, 0.8);
  border-radius: 20px;
  color: white;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  max-width: 400px;
  animation: dropZonePulse 1.5s ease-in-out infinite;

  @keyframes dropZonePulse {
    0%,
    100% {
      transform: scale(1);
      border-color: rgba(59, 130, 246, 0.8);
    }
    50% {
      transform: scale(1.02);
      border-color: rgba(59, 130, 246, 1);
    }
  }

  .icon {
    font-size: 56px;
    color: #3b82f6;
    margin-bottom: 20px;
    display: block;
    animation: iconBounce 1s ease-in-out infinite;
  }

  @keyframes iconBounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-4px);
    }
  }
`

const LoadingOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
`

const LoadingMessage = styled.div`
  padding: 32px 48px;
  background: rgba(26, 26, 26, 0.95);
  border: 2px solid rgba(59, 130, 246, 0.6);
  border-radius: 16px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-width: 350px;

  .spinner {
    font-size: 32px;
    color: #3b82f6;
    margin-bottom: 16px;
    display: block;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .progress-dots {
    display: inline-flex;
    gap: 4px;
    margin-left: 8px;
  }

  .dot {
    width: 4px;
    height: 4px;
    background: #3b82f6;
    border-radius: 50%;
    animation: dotPulse 1.4s ease-in-out infinite both;
  }

  .dot:nth-child(1) {
    animation-delay: -0.32s;
  }
  .dot:nth-child(2) {
    animation-delay: -0.16s;
  }
  .dot:nth-child(3) {
    animation-delay: 0s;
  }

  @keyframes dotPulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
    }
    40% {
      opacity: 1;
    }
  }
`

interface MapContainerProps {
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void
  onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void
  onDragEnter?: (event: React.DragEvent<HTMLDivElement>) => void
  onDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void
  isDragging?: boolean
  isDimmed?: boolean
  isLoading?: boolean
  children: React.ReactNode
}

const MapContainer: React.FC<MapContainerProps> = ({
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
  isDragging,
  isDimmed,
  isLoading,
  children,
}) => {
  return (
    <MapContainerStyled
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      $isDimmed={isDimmed}
    >
      {children}
      {isDragging && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '8px',
              color: 'black',
              fontSize: '1.2em',
            }}
          >
            Drop TIFF file here
          </div>
        </div>
      )}

      {/* Enhanced Drop Zone Overlay */}
      <DropZoneOverlay $isVisible={isDimmed || false}>
        <DropZoneMessage>
          <i className="bi bi-cloud-upload icon"></i>
          Drop your TIFF file here for inference
          <div style={{ fontSize: '14px', marginTop: '8px', opacity: 0.8 }}>
            The file will be uploaded and inference will run automatically
          </div>
        </DropZoneMessage>
      </DropZoneOverlay>

      {/* Loading Overlay */}
      <LoadingOverlay $isVisible={isLoading || false}>
        <LoadingMessage>
          <i className="bi bi-cpu spinner"></i>
          Running Inference
          <div className="progress-dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
          <div style={{ fontSize: '14px', marginTop: '12px', opacity: 0.8 }}>
            Processing your TIFF file...
          </div>
        </LoadingMessage>
      </LoadingOverlay>

      <div id="map" />
    </MapContainerStyled>
  )
}

export default MapContainer
